const BigNumber = require('bignumber.js');
const Cart = require('@/libraries/DigiTickets/Cart/Cart');
const CartLine = require('@/lib/Cart/CartLine');
const ItemType = require('@/lib/Items/ItemType');
const mergeDiscountedCartLines = require('@/libraries/DigiTickets/Cart/mergeDiscountedCartLines');
const Offer = require('@/lib/Offers/Offer');
const VariableDiscount = require('@/models/VariableDiscount');
const VariableDiscountsCollection = require('@/models/VariableDiscountsCollection');
const { cloneDeep } = require('@/functions/clone');
const { container } = require('@/container');
const { getOrderLineEditableStatus } = require('@/lib/Orders/orderLineFunctions');
const { updateChildLineReferences } = require('@/lib/Cart');

/**
 * @param {Order} order
 *
 * @return {Cart}
 */
const mapOrderToCart = async (order) => {
    const cart = new Cart();

    cart.customer = cloneDeep(order.customer);
    cart.originalOrder = order;
    cart.modifiedAt = order.date;
    cart.staffRef = order.staffRef;
    cart.notes = order.notes;
    cart.giftAid = order.giftAid;
    cart.giftAidPrices = order.giftAid;
    cart.groupBooking = order.groupBooking;

    // thirdPartyID and thirdPartyRef are not copied so that the edited order and any new payments get new refs
    // generated so that payments from and original order and an edit of that order cannot share the same thirdPartyID.

    // Payments are not copied to the cart because that would result in calculation errors and potentially duplicating
    // payments. Instead, you can use access cart.originalOrder.payments

    /**
     * Build a list of which cart-level offers should be enabled.
     * The keys are offerIDs and the values are the number of times we've found this offer applied in the lines.
     *
     * @type {Object<number, number>}
     */
    const selectedCartLevelManualOfferIDs = {};

    /**
     * Build a list of cart-level coupon offer codes.
     * The keys are offerIDs and the values the actual coupon code used with this discount.
     *
     * @type {Object<number, string>}
     */
    const selectedCartLevelCouponOfferCodes = {};

    for (const orderLine of order.items) {
        let item;

        // The orders API does return an 'item' and an 'event' attached to each line, but that item does not contain
        // some additional sub-relations like 'category', 'event', and 'giftAidRate'.
        // Attempt to load the item from the local IndexedDB if available, and fall back to using the
        // version that came from the API.

        // Note: IndexedDB only fetches items where items.status = 'Active' and items.paymentChannelID = 4.
        // This means that items that are not sellable in ProPoint will not be available in IndexedDB but we
        // may still need to deal with them when editing an order.
        try {
            item = await container.itemRepository.getItem(orderLine.itemType, orderLine.itemID);
        } catch (e) {
            console.warn('Failed to load item from repository.', orderLine.itemType, orderLine.itemID, e);

            // Fallback to using the item from the order.
            item = orderLine.item;
            item.event = orderLine.event;

            if (item.giftAidRateID) {
                // The GiftAidRate isn't available on the order but, we can fetch it from IndexedDB instead.
                item.giftAidRate = await container.giftAidRateRepository.find(item.giftAidRateID);
            }
        }

        let cartLine = new CartLine(null, item, orderLine.branch);

        cartLine.lineNumber = orderLine.lineNumber;
        cartLine.session = orderLine.session;
        cartLine.quantity = orderLine.qty;
        cartLine.quantityDisplay = orderLine.qty;
        cartLine.editableStatus = getOrderLineEditableStatus(orderLine, item);
        cartLine.itemInstances = cloneDeep(orderLine.iteminstances);

        cartLine.price = orderLine.price;
        cartLine.bookingFee = orderLine.bookingFee;

        cartLine.customPrice = orderLine.price - orderLine.donation;

        cartLine.giftAid = orderLine.giftAid;
        cartLine.donation = orderLine.donation;

        // There is no specific flag to tell if an order was made with a donation rather than Gift Aid
        // but, we can infer it if a line contains a donation.
        if (cartLine.donation !== 0 && !cart.giftAidPrices) {
            cart.giftAidPrices = true;
        }

        cartLine.ordersItemsID = orderLine.ID;
        cartLine.redeemed = orderLine.redeemed;
        cartLine.minimumQuantity = orderLine.redeemed;
        cartLine.qtyBeforeAdjustment = orderLine.iteminstances.length;
        cartLine.soldGiftVoucherData = orderLine.soldGiftVoucherData;

        cartLine.originalOrderLines = [
            orderLine,
        ];

        // Handle discounts/offers from the order line.
        //
        // There are two parts to consider:
        // - The list of 'discounts' (CartItemDiscount[]) on each line.
        //   These are the results of some calculations are should be able to be regenerated.
        // - The properties and fields that are looked at to generate those discounts on each line.
        //   These need to be set correctly so that discounts don't get lost when recalculation happens.
        //
        // We need to set up the cart lines so that the discounts will get reapplied when cart calculation runs.
        // If we just add them to the list of discounts they will get lost on the next calculation, or worse look
        // like they are applied but not actually be applied.
        //
        // Potential issues:
        // - If the offer configuration has changed since the order was made, the new offer config will be applied
        //   and the discount that generates may not be the same.
        // - If a customer used an online discount code that wouldn't get reapplied here and that would cause the
        //   discount amount to change. See https://digitickets.atlassian.net/browse/PROP-79
        if (orderLine.discount) {
            /**
             * @type {CartItemDiscount}
             */
            const cartItemDiscount = cloneDeep(orderLine.discount);

            // If this was a discount applied during a sale in the Back Office there will be a discount amount,
            // but no offerID or offer. In this case we can treat it like a manual line-level discount.
            if (!cartItemDiscount.offer?.ID) {
                console.warn(
                    'Order line discount does not have an offer attached, treating as manual line-level discount.',
                    orderLine.lineNumber,
                    cartItemDiscount
                );
                const offer = new Offer();
                offer.name = 'Manual Discount';
                offer.trigger = 'manualLine'; // This is important to set so it's treated as a manual line-level offer.
                offer.target = 'anyItems';
                offer.valueType = 'value';
                // orderLine.discountAmount is positive, and the offer value should also be positive.
                // However, the discountAmount is for the whole line. We need the value to be the amount per item.
                offer.value = new BigNumber(orderLine.discountAmount).dividedBy(orderLine.getQuantity()).toNumber();
                cartItemDiscount.offer = offer;
            }

            const isManualLineOffer = cartItemDiscount.offer?.isManuallyAppliedToLine();
            const isManualCartOffer = cartItemDiscount.offer?.isManuallyAppliedToCart();
            const isCouponCodeOffer = cartItemDiscount.offer?.isCouponCodeOffer();

            if (isManualLineOffer) {
                // Set this flag before adding the discount to the cart line, otherwise it will get stuck and
                // appear as a crossed out / disabled discount on the line.
                cartItemDiscount.manual = true;
            }

            // Add the discount to the cart line.
            cartLine.discounts.push(cartItemDiscount);

            // Now the discount has been added set up the line / cart so it won't get lost.
            if (isManualLineOffer) {
                // Manual line-level discount:
                // Set it as the manual offer for the line.
                cartLine.setManualOffer(cartItemDiscount.offer);
            } else if (isManualCartOffer || isCouponCodeOffer) {
                // Manual cart-level discount:
                // We need to enable this offer at the cart level so that it remains selected when
                // the offer recalculation happens.
                if (!selectedCartLevelManualOfferIDs.hasOwnProperty(cartItemDiscount.offer.ID)) {
                    selectedCartLevelManualOfferIDs[cartItemDiscount.offer.ID] = 0;
                }
                selectedCartLevelManualOfferIDs[cartItemDiscount.offer.ID] += orderLine.getQuantity();

                if (isCouponCodeOffer && cartItemDiscount.offer.code) {
                    selectedCartLevelCouponOfferCodes[cartItemDiscount.offer.ID] = cartItemDiscount.offer.code;
                }
            }

            if (cartItemDiscount.isVariable()) {
                // For variable discounts the amount needs to go into the VariableDiscountsCollection
                // on the cart line instead of in the CartItemDiscount.
                // We will end up losing the reason entered for the discount in this process because
                // the API doesn't return it! But the API doesn't save the reasons for variable discounts
                // when editing an order anyway, so ¯\_(ツ)_/¯
                // This is better than losing the discount amount and the total being wrong.
                const variableDiscount = new VariableDiscount(cartItemDiscount.offerID);
                variableDiscount.setAmount(
                    new BigNumber(cartItemDiscount.amount)
                        .dividedBy(cartItemDiscount.qty)
                        .toNumber()
                );
                const vdc = new VariableDiscountsCollection();
                vdc.addVariableDiscount(variableDiscount);
                cartLine.setVariableDiscountsCollection(vdc);
                // Clear the amount on the discount now that it is on the variable discount.
                cartItemDiscount.amount = 0;
            }

            // TODO: https://digitickets.atlassian.net/browse/PROP-79 online offers will get lost here because
            // we can't enable those from ProPoint.
        }

        // Copy group booking references back to the cart.
        if (orderLine.bookingGroup) {
            cartLine.groupBookingReference = orderLine.bookingGroup.name;
        }

        if (cartLine.getItemType() === ItemType.ITEM_OPTION && orderLine.optionSetID) {
            try {
                if (orderLine.optionSet) {
                    // The order line might have already had the optionSet set on it.
                    cartLine.optionSet = orderLine.optionSet;
                } else {
                    // If not, load it from the repository.
                    cartLine.optionSet = await container.optionSetRepository.find(orderLine.optionSetID);
                }
            } catch (e) {
                console.warn(`Failed to load option set ${orderLine.optionSetID} for order line ${orderLine.lineNumber}`, e);
            }
        }

        cart.itemList.push(cartLine);
    }

    // Now all the order lines have been converted to cart lines, set the parentThirdPartyRef on each applicable line.
    for (const orderLine of order.items) {
        // Note we are iterating over order.items again here so that we don't need to copy parentID to the cart line.
        if (orderLine.parentID) {
            // Find the corresponding cart line.
            const cartLine = cart.itemList.find((cl) => cl.ordersItemsID === orderLine.ID);

            // Find the corresponding parent cart line.
            const parentCartLine = cart.itemList.find((cl) => cl.ordersItemsID === orderLine.parentID);

            if (cartLine && parentCartLine) {
                cartLine.parentThirdPartyRef = parentCartLine.thirdPartyRef;
            }
        }
    }
    // Now ensure that the linkage between parent and child lines is set, otherwise when we call the merge
    // function below it will break item option lines, because the options wouldn't be present in the line IDs.
    updateChildLineReferences(cart);

    // New style field data.
    // Item level field data has already been copied to the cart by cloning the itemInstances
    // Copy the order level data from the order to the cart.
    cart.fields = cloneDeep(order.getOrderLevelFieldGroup());

    // Merge together discounted lines.
    mergeDiscountedCartLines(cart);

    // selectedManualOfferIDs is an object where the key is offerID and the value is number of times it is applied e.g.
    // { 1: 3, 4: 1 } which is sent to the /v2/cartcalculations endpoint by the refreshExternalCalculations() method
    // if we don't re-attach them, then despite the discount/offer details appearing on the cartLine they will get
    // stripped out and removed.
    cart.selectedManualOfferIDs = { ...selectedCartLevelManualOfferIDs };
    cart.selectedCouponCodeOfferCodes = { ...selectedCartLevelCouponOfferCodes };

    // Complete setting up the cartLine so it can be used in the contentHash.
    cart.itemList.forEach((cartLine) => {
        cartLine.calculate(false);
    });

    // Create a hash of the initial state of each line in the cart so we can compare with the current state later.
    // This is done last so any calculations above don't change the hash in the meantime.
    cart.itemList.forEach((cartLine) => {
        cartLine.contentHashInitial = cartLine.getContentHash();
    });

    // This has to be the last action because it is a hash of the state of the entire cart.
    // Create a hash of the initial state of the cart, so we can compare with the current state later.
    cart.contentHashInitial = cart.getContentHash();

    return cart;
};

module.exports = mapOrderToCart;
